import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TJotformWarning } from 'redux/reducers/jotform/types';
import {
	TCreateOrUpdatePpwrFeeRequest,
	TCreateOrUpdatePpwrPackagingTypeRequest,
	TPpwrEmSettings,
	TPpwrEmSettingsIdRequest,
	TPpwrEmSettingsListResponse,
	TPpwrEmSettingsSearchRequest,
	TUpdatePpwrEmSettingsRequest,
	TPpwrFee,
	TPpwrFeeIdRequest,
	TPpwrFeeListResponse,
	TPpwrFeeSearchRequest,
	TUpdatePpwrFeeRequest,
	TPpwrPackagingType,
	TPpwrPackagingTypeIdRequest,
	TPpwrPackagingTypeListResponse,
	TPpwrPackagingTypeSearchRequest,
	TPpwrStore,
	TUpdatePpwrPackagingTypeHandlerRequest,
	TUpdatePpwrPackagingTypeRequest,
	TActivatePpwrServiceRequest,
	TPpwrJotform,
	TPpwrJotformIdRequest,
	TPpwrJotformListResponse,
	TPpwrJotformSearchRequest,
	TDownloadPpwrEmPackagingTypeDocumentRequest,
	TPpwrEmPackagingTypeDocumentRequest,
	TPpwrEmPackagingTypeListResponse,
	TPpwrEmPackagingTypeSearchRequest,
	TUpdatePpwrEmPackagingTypeRequest,
	TUploadPpwrEmPackagingTypeDocumentRequest,
	TCreateOrUpdatePpwrEmCountryExclusionRequest,
	TPpwrEmCountryExclusionListResponse,
	TPpwrEmCountryExclusionSearchRequest,
	TUpdatePpwrEmCountryExclusionRequest,
} from './types';

// ==========================================:
export const initialState: TPpwrStore = {
	packagingTypes: null,
	packagingTypesLoading: false,
	lastPackagingTypesParams: null,
	packagingTypeOptions: null,
	packagingTypeOptionsLoading: false,
	packagingTypeDetails: null,
	packagingTypeDetailsLoading: false,
	packagingTypeSaveLoading: false,
	packagingTypeSaveSuccess: false,
	fees: null,
	feesLoading: false,
	lastFeesParams: null,
	feeDetails: null,
	feeDetailsLoading: false,
	feeSaveLoading: false,
	feeSaveSuccess: false,
	emSettings: null,
	emSettingsLoading: false,
	lastEmSettingsParams: null,
	emSettingsDetails: null,
	emSettingsDetailsLoading: false,
	emSettingsSaveLoading: false,
	emSettingsSaveSuccess: false,
	jotforms: null,
	jotformsLoading: false,
	lastJotformsParams: null,
	jotformDetails: null,
	jotformDetailsLoading: false,
	activateServiceLoading: false,
	activateServiceSuccess: false,
	activateServiceWarning: null,
	feeOptions: null,
	feeOptionsLoading: false,
	emPackagingTypes: null,
	emPackagingTypesLoading: false,
	lastEmPackagingTypesParams: null,
	emPackagingTypeSaveLoading: false,
	emPackagingTypeSaveSuccess: false,
	emPackagingTypeDocumentUploadLoading: false,
	downloadingEmPackagingTypeDocumentId: null,
	deletingEmPackagingTypeDocumentId: null,
	emCountryExclusions: null,
	emCountryExclusionsLoading: false,
	lastEmCountryExclusionsParams: null,
	emCountryExclusionSaveLoading: false,
	emCountryExclusionSaveSuccess: false,
};

// ==========================================:
const ppwrSlice = createSlice({
	name: '@@ppwr',
	initialState,
	reducers: {
		searchPpwrPackagingTypesRequest: (
			state,
			action: PayloadAction<TPpwrPackagingTypeSearchRequest>,
		) => {
			state.packagingTypesLoading = true;
			state.lastPackagingTypesParams = action.payload;
		},
		searchPpwrPackagingTypesSuccess: (
			state,
			action: PayloadAction<TPpwrPackagingTypeListResponse>,
		) => {
			state.packagingTypes = action.payload;
			state.packagingTypesLoading = false;
		},
		searchPpwrPackagingTypesFailure: (state) => {
			state.packagingTypesLoading = false;
		},

		getPpwrPackagingTypeOptionsRequest: (state) => {
			state.packagingTypeOptionsLoading = true;
		},
		getPpwrPackagingTypeOptionsSuccess: (state, action: PayloadAction<TPpwrPackagingType[]>) => {
			state.packagingTypeOptions = action.payload;
			state.packagingTypeOptionsLoading = false;
		},
		getPpwrPackagingTypeOptionsFailure: (state) => {
			state.packagingTypeOptionsLoading = false;
		},

		getPpwrPackagingTypeRequest: (state, _action: PayloadAction<TPpwrPackagingTypeIdRequest>) => {
			state.packagingTypeDetailsLoading = true;
		},
		getPpwrPackagingTypeSuccess: (state, action: PayloadAction<TPpwrPackagingType>) => {
			state.packagingTypeDetails = action.payload;
			state.packagingTypeDetailsLoading = false;
		},
		getPpwrPackagingTypeFailure: (state) => {
			state.packagingTypeDetailsLoading = false;
		},
		clearPpwrPackagingTypeDetails: (state) => {
			state.packagingTypeDetails = null;
		},

		createPpwrPackagingTypeRequest: (
			state,
			_action: PayloadAction<TCreateOrUpdatePpwrPackagingTypeRequest>,
		) => {
			state.packagingTypeSaveLoading = true;
			state.packagingTypeSaveSuccess = false;
		},
		updatePpwrPackagingTypeRequest: (
			state,
			_action: PayloadAction<TUpdatePpwrPackagingTypeRequest>,
		) => {
			state.packagingTypeSaveLoading = true;
			state.packagingTypeSaveSuccess = false;
		},
		updatePpwrPackagingTypeHandlerRequest: (
			state,
			_action: PayloadAction<TUpdatePpwrPackagingTypeHandlerRequest>,
		) => {
			state.packagingTypeSaveLoading = true;
			state.packagingTypeSaveSuccess = false;
		},
		savePpwrPackagingTypeSuccess: (state) => {
			state.packagingTypeSaveLoading = false;
			state.packagingTypeSaveSuccess = true;
		},
		savePpwrPackagingTypeFailure: (state) => {
			state.packagingTypeSaveLoading = false;
		},
		resetSavePpwrPackagingTypeSuccess: (state) => {
			state.packagingTypeSaveSuccess = false;
		},

		searchPpwrFeesRequest: (state, action: PayloadAction<TPpwrFeeSearchRequest>) => {
			state.feesLoading = true;
			state.lastFeesParams = action.payload;
		},
		searchPpwrFeesSuccess: (state, action: PayloadAction<TPpwrFeeListResponse>) => {
			state.fees = action.payload;
			state.feesLoading = false;
		},
		searchPpwrFeesFailure: (state) => {
			state.feesLoading = false;
		},

		getPpwrFeeRequest: (state, _action: PayloadAction<TPpwrFeeIdRequest>) => {
			state.feeDetailsLoading = true;
		},
		getPpwrFeeSuccess: (state, action: PayloadAction<TPpwrFee>) => {
			state.feeDetails = action.payload;
			state.feeDetailsLoading = false;
		},
		getPpwrFeeFailure: (state) => {
			state.feeDetailsLoading = false;
		},
		clearPpwrFeeDetails: (state) => {
			state.feeDetails = null;
		},

		createPpwrFeeRequest: (state, _action: PayloadAction<TCreateOrUpdatePpwrFeeRequest>) => {
			state.feeSaveLoading = true;
			state.feeSaveSuccess = false;
		},
		updatePpwrFeeRequest: (state, _action: PayloadAction<TUpdatePpwrFeeRequest>) => {
			state.feeSaveLoading = true;
			state.feeSaveSuccess = false;
		},
		savePpwrFeeSuccess: (state) => {
			state.feeSaveLoading = false;
			state.feeSaveSuccess = true;
		},
		savePpwrFeeFailure: (state) => {
			state.feeSaveLoading = false;
		},
		resetSavePpwrFeeSuccess: (state) => {
			state.feeSaveSuccess = false;
		},

		searchPpwrEmSettingsRequest: (state, action: PayloadAction<TPpwrEmSettingsSearchRequest>) => {
			state.emSettingsLoading = true;
			state.lastEmSettingsParams = action.payload;
		},
		searchPpwrEmSettingsSuccess: (state, action: PayloadAction<TPpwrEmSettingsListResponse>) => {
			state.emSettings = action.payload;
			state.emSettingsLoading = false;
		},
		searchPpwrEmSettingsFailure: (state) => {
			state.emSettingsLoading = false;
		},

		getPpwrEmSettingsRequest: (state, _action: PayloadAction<TPpwrEmSettingsIdRequest>) => {
			state.emSettingsDetailsLoading = true;
		},
		getPpwrEmSettingsSuccess: (state, action: PayloadAction<TPpwrEmSettings>) => {
			state.emSettingsDetails = action.payload;
			state.emSettingsDetailsLoading = false;
		},
		getPpwrEmSettingsFailure: (state) => {
			state.emSettingsDetailsLoading = false;
		},
		clearPpwrEmSettingsDetails: (state) => {
			state.emSettingsDetails = null;
		},

		updatePpwrEmSettingsRequest: (state, _action: PayloadAction<TUpdatePpwrEmSettingsRequest>) => {
			state.emSettingsSaveLoading = true;
			state.emSettingsSaveSuccess = false;
		},
		savePpwrEmSettingsSuccess: (state) => {
			state.emSettingsSaveLoading = false;
			state.emSettingsSaveSuccess = true;
		},
		savePpwrEmSettingsFailure: (state) => {
			state.emSettingsSaveLoading = false;
		},
		resetSavePpwrEmSettingsSuccess: (state) => {
			state.emSettingsSaveSuccess = false;
		},

		searchPpwrJotformsRequest: (state, action: PayloadAction<TPpwrJotformSearchRequest>) => {
			state.jotformsLoading = true;
			state.lastJotformsParams = action.payload;
		},
		searchPpwrJotformsSuccess: (state, action: PayloadAction<TPpwrJotformListResponse>) => {
			state.jotforms = action.payload;
			state.jotformsLoading = false;
		},
		searchPpwrJotformsFailure: (state) => {
			state.jotformsLoading = false;
		},

		getPpwrJotformRequest: (state, _action: PayloadAction<TPpwrJotformIdRequest>) => {
			state.jotformDetailsLoading = true;
		},
		getPpwrJotformSuccess: (state, action: PayloadAction<TPpwrJotform>) => {
			state.jotformDetails = action.payload;
			state.jotformDetailsLoading = false;
		},
		getPpwrJotformFailure: (state) => {
			state.jotformDetailsLoading = false;
		},
		clearPpwrJotformDetails: (state) => {
			state.jotformDetails = null;
		},

		activatePpwrServiceRequest: (state, _action: PayloadAction<TActivatePpwrServiceRequest>) => {
			state.activateServiceLoading = true;
			state.activateServiceSuccess = false;
			state.activateServiceWarning = null;
		},
		activatePpwrServiceSuccess: (state) => {
			state.activateServiceLoading = false;
			state.activateServiceSuccess = true;
		},
		activatePpwrServiceWarning: (state, action: PayloadAction<TJotformWarning>) => {
			state.activateServiceLoading = false;
			state.activateServiceWarning = action.payload;
		},
		activatePpwrServiceFailure: (state) => {
			state.activateServiceLoading = false;
		},
		resetActivatePpwrService: (state) => {
			state.activateServiceLoading = false;
			state.activateServiceSuccess = false;
			state.activateServiceWarning = null;
		},

		getPpwrFeeOptionsRequest: (state) => {
			state.feeOptionsLoading = true;
		},
		getPpwrFeeOptionsSuccess: (state, action: PayloadAction<TPpwrFee[]>) => {
			state.feeOptions = action.payload;
			state.feeOptionsLoading = false;
		},
		getPpwrFeeOptionsFailure: (state) => {
			state.feeOptionsLoading = false;
		},

		searchPpwrEmPackagingTypesRequest: (
			state,
			action: PayloadAction<TPpwrEmPackagingTypeSearchRequest>,
		) => {
			state.emPackagingTypesLoading = true;
			state.lastEmPackagingTypesParams = action.payload;
		},
		searchPpwrEmPackagingTypesSuccess: (
			state,
			action: PayloadAction<TPpwrEmPackagingTypeListResponse>,
		) => {
			state.emPackagingTypes = action.payload;
			state.emPackagingTypesLoading = false;
		},
		searchPpwrEmPackagingTypesFailure: (state) => {
			state.emPackagingTypesLoading = false;
		},
		clearPpwrEmPackagingTypes: (state) => {
			state.emPackagingTypes = null;
			state.lastEmPackagingTypesParams = null;
		},

		updatePpwrEmPackagingTypeRequest: (
			state,
			_action: PayloadAction<TUpdatePpwrEmPackagingTypeRequest>,
		) => {
			state.emPackagingTypeSaveLoading = true;
			state.emPackagingTypeSaveSuccess = false;
		},
		updatePpwrEmPackagingTypeSuccess: (state) => {
			state.emPackagingTypeSaveLoading = false;
			state.emPackagingTypeSaveSuccess = true;
		},
		updatePpwrEmPackagingTypeFailure: (state) => {
			state.emPackagingTypeSaveLoading = false;
		},
		resetUpdatePpwrEmPackagingTypeSuccess: (state) => {
			state.emPackagingTypeSaveSuccess = false;
		},

		uploadPpwrEmPackagingTypeDocumentRequest: (
			state,
			_action: PayloadAction<TUploadPpwrEmPackagingTypeDocumentRequest>,
		) => {
			state.emPackagingTypeDocumentUploadLoading = true;
		},
		uploadPpwrEmPackagingTypeDocumentFinish: (state) => {
			state.emPackagingTypeDocumentUploadLoading = false;
		},

		downloadPpwrEmPackagingTypeDocumentRequest: (
			state,
			action: PayloadAction<TDownloadPpwrEmPackagingTypeDocumentRequest>,
		) => {
			state.downloadingEmPackagingTypeDocumentId = action.payload.documentId;
		},
		downloadPpwrEmPackagingTypeDocumentFinish: (state) => {
			state.downloadingEmPackagingTypeDocumentId = null;
		},

		deletePpwrEmPackagingTypeDocumentRequest: (
			state,
			action: PayloadAction<TPpwrEmPackagingTypeDocumentRequest>,
		) => {
			state.deletingEmPackagingTypeDocumentId = action.payload.documentId;
		},
		deletePpwrEmPackagingTypeDocumentFinish: (state) => {
			state.deletingEmPackagingTypeDocumentId = null;
		},

		searchPpwrEmCountryExclusionsRequest: (
			state,
			action: PayloadAction<TPpwrEmCountryExclusionSearchRequest>,
		) => {
			state.emCountryExclusionsLoading = true;
			state.lastEmCountryExclusionsParams = action.payload;
		},
		searchPpwrEmCountryExclusionsSuccess: (
			state,
			action: PayloadAction<TPpwrEmCountryExclusionListResponse>,
		) => {
			state.emCountryExclusions = action.payload;
			state.emCountryExclusionsLoading = false;
		},
		searchPpwrEmCountryExclusionsFailure: (state) => {
			state.emCountryExclusionsLoading = false;
		},
		clearPpwrEmCountryExclusions: (state) => {
			state.emCountryExclusions = null;
			state.lastEmCountryExclusionsParams = null;
		},

		createPpwrEmCountryExclusionRequest: (
			state,
			_action: PayloadAction<TCreateOrUpdatePpwrEmCountryExclusionRequest>,
		) => {
			state.emCountryExclusionSaveLoading = true;
			state.emCountryExclusionSaveSuccess = false;
		},
		updatePpwrEmCountryExclusionRequest: (
			state,
			_action: PayloadAction<TUpdatePpwrEmCountryExclusionRequest>,
		) => {
			state.emCountryExclusionSaveLoading = true;
			state.emCountryExclusionSaveSuccess = false;
		},
		savePpwrEmCountryExclusionSuccess: (state) => {
			state.emCountryExclusionSaveLoading = false;
			state.emCountryExclusionSaveSuccess = true;
		},
		savePpwrEmCountryExclusionFailure: (state) => {
			state.emCountryExclusionSaveLoading = false;
		},
		resetSavePpwrEmCountryExclusionSuccess: (state) => {
			state.emCountryExclusionSaveSuccess = false;
		},

		ppwrInitState: () => initialState,
	},
});

export default ppwrSlice.reducer;
export const {
	searchPpwrPackagingTypesRequest,
	searchPpwrPackagingTypesSuccess,
	searchPpwrPackagingTypesFailure,
	getPpwrPackagingTypeOptionsRequest,
	getPpwrPackagingTypeOptionsSuccess,
	getPpwrPackagingTypeOptionsFailure,
	getPpwrPackagingTypeRequest,
	getPpwrPackagingTypeSuccess,
	getPpwrPackagingTypeFailure,
	clearPpwrPackagingTypeDetails,
	createPpwrPackagingTypeRequest,
	updatePpwrPackagingTypeRequest,
	updatePpwrPackagingTypeHandlerRequest,
	savePpwrPackagingTypeSuccess,
	savePpwrPackagingTypeFailure,
	resetSavePpwrPackagingTypeSuccess,
	searchPpwrFeesRequest,
	searchPpwrFeesSuccess,
	searchPpwrFeesFailure,
	getPpwrFeeRequest,
	getPpwrFeeSuccess,
	getPpwrFeeFailure,
	clearPpwrFeeDetails,
	createPpwrFeeRequest,
	updatePpwrFeeRequest,
	savePpwrFeeSuccess,
	savePpwrFeeFailure,
	resetSavePpwrFeeSuccess,
	searchPpwrEmSettingsRequest,
	searchPpwrEmSettingsSuccess,
	searchPpwrEmSettingsFailure,
	getPpwrEmSettingsRequest,
	getPpwrEmSettingsSuccess,
	getPpwrEmSettingsFailure,
	clearPpwrEmSettingsDetails,
	updatePpwrEmSettingsRequest,
	savePpwrEmSettingsSuccess,
	savePpwrEmSettingsFailure,
	resetSavePpwrEmSettingsSuccess,
	searchPpwrJotformsRequest,
	searchPpwrJotformsSuccess,
	searchPpwrJotformsFailure,
	getPpwrJotformRequest,
	getPpwrJotformSuccess,
	getPpwrJotformFailure,
	clearPpwrJotformDetails,
	activatePpwrServiceRequest,
	activatePpwrServiceSuccess,
	activatePpwrServiceWarning,
	activatePpwrServiceFailure,
	resetActivatePpwrService,
	getPpwrFeeOptionsRequest,
	getPpwrFeeOptionsSuccess,
	getPpwrFeeOptionsFailure,
	searchPpwrEmPackagingTypesRequest,
	searchPpwrEmPackagingTypesSuccess,
	searchPpwrEmPackagingTypesFailure,
	clearPpwrEmPackagingTypes,
	updatePpwrEmPackagingTypeRequest,
	updatePpwrEmPackagingTypeSuccess,
	updatePpwrEmPackagingTypeFailure,
	resetUpdatePpwrEmPackagingTypeSuccess,
	uploadPpwrEmPackagingTypeDocumentRequest,
	uploadPpwrEmPackagingTypeDocumentFinish,
	downloadPpwrEmPackagingTypeDocumentRequest,
	downloadPpwrEmPackagingTypeDocumentFinish,
	deletePpwrEmPackagingTypeDocumentRequest,
	deletePpwrEmPackagingTypeDocumentFinish,
	searchPpwrEmCountryExclusionsRequest,
	searchPpwrEmCountryExclusionsSuccess,
	searchPpwrEmCountryExclusionsFailure,
	clearPpwrEmCountryExclusions,
	createPpwrEmCountryExclusionRequest,
	updatePpwrEmCountryExclusionRequest,
	savePpwrEmCountryExclusionSuccess,
	savePpwrEmCountryExclusionFailure,
	resetSavePpwrEmCountryExclusionSuccess,
	ppwrInitState,
} = ppwrSlice.actions;
